import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type CellComponentProps, Grid } from "react-window";
import { BEGIN_TIME_STRING, END_TIME_STRING } from "../../../utils";
import type { Temporal, TemporalValue as TemporalValueType } from "../../../utils";
import AttributeValue from "../AttributeValue/AttributeValue";
import { formatDate } from "./utils";

export type TemporalTextProps = {
  value: TemporalValueType | Temporal;
  maxEntries?: number;
};

const DEFAULT_CELL_WIDTH = 160;
const SCROLL_PADDING_Y = 12;
const VIRTUALIZED_THRESHOLD = 25;

const CELL_INDEX_ATTRIBUTE = "data-deci-temporal-cell-index";

type DynamicCellWidth = {
  getAverageCellWidth(): number;
  getCellWidth(index: number): number | undefined;
  observeCellElements: (elements: Element[] | NodeListOf<Element>) => () => void;
  setCellWidth(index: number, size: number): void;
};

const useDynamicCellWidth = ({
  defaultCellWidth,
  key,
}: {
  defaultCellWidth: (index: number) => number;
  key?: string;
}): DynamicCellWidth => {
  const [state, setState] = useState<{ key?: string; map: Map<number, number> }>({
    key,
    map: new Map(),
  });

  if (state.key !== key) {
    setState({ key, map: new Map() });
  }

  const { map } = state;
  const getAverageCellWidth = useCallback(() => {
    let total = 0;
    for (const width of map.values()) {
      total += width;
    }
    return map.size === 0 ? defaultCellWidth(0) : total / map.size;
  }, [defaultCellWidth, map]);

  const getCellWidth = useCallback(
    (index: number) => {
      const width = map.get(index);
      if (width !== undefined) return width;

      const defaultWidth = defaultCellWidth(index);
      map.set(index, defaultWidth);
      return defaultWidth;
    },
    [defaultCellWidth, map],
  );

  const setCellWidth = useCallback((index: number, size: number) => {
    setState((prev) => {
      if (prev.map.get(index) === size) return prev;

      const map = new Map(prev.map);
      map.set(index, size);
      return { ...prev, map };
    });
  }, []);

  const resizeObserver = useMemo(() => {
    if (typeof ResizeObserver === "undefined") return undefined;

    return new ResizeObserver((entries) => {
      for (const entry of entries) {
        const indexRaw = entry.target.getAttribute(CELL_INDEX_ATTRIBUTE);
        if (indexRaw === null) continue;

        const index = Number.parseInt(indexRaw, 10);
        const width = entry.borderBoxSize[0]?.inlineSize ?? entry.target.getBoundingClientRect().width;
        if (width) setCellWidth(index, width);
      }
    });
  }, [setCellWidth]);

  useEffect(() => {
    if (!resizeObserver) return undefined;

    return () => resizeObserver.disconnect();
  }, [resizeObserver]);

  const observeCellElements = useCallback(
    (elements: Element[] | NodeListOf<Element>) => {
      if (!resizeObserver) return () => {};

      for (const element of elements) {
        resizeObserver.observe(element);
      }
      return () => {
        for (const element of elements) {
          resizeObserver.unobserve(element);
        }
      };
    },
    [resizeObserver],
  );

  return useMemo(
    () => ({
      getAverageCellWidth,
      getCellWidth,
      observeCellElements,
      setCellWidth,
    }),
    [getAverageCellWidth, getCellWidth, observeCellElements, setCellWidth],
  );
};

const TemporalRangeContent = ({ range, index }: { range: Temporal["ranges"][number]; index: number }) => {
  const prop = { node: { input: range.v }, showType: false } as const;

  if (range.r.start === BEGIN_TIME_STRING && range.r.end === END_TIME_STRING) {
    return (
      <>
        Always <AttributeValue {...prop} />
      </>
    );
  }

  if (index === 0) {
    return <AttributeValue {...prop} />;
  }

  return (
    <>
      {formatDate(range.r.start)}: <AttributeValue {...prop} />
    </>
  );
};

const TemporalRangeShell = ({
  children,
  cellIndex,
  isFirst,
  isLast,
  measureRef,
}: {
  cellIndex?: number;
  children: React.ReactNode;
  isFirst: boolean;
  isLast: boolean;
  measureRef?: React.Ref<HTMLSpanElement>;
}) => (
  <span
    ref={measureRef}
    {...(typeof cellIndex === "number" ? { [CELL_INDEX_ATTRIBUTE]: cellIndex } : {})}
    style={{
      display: "inline-block",
      whiteSpace: "nowrap",
      paddingLeft: isFirst ? 0 : 8,
      paddingRight: isLast ? 0 : 8,
      marginRight: isLast ? 0 : 8,
      borderRight: isLast ? "none" : "1px solid currentColor",
    }}
  >
    {children}
  </span>
);

const TemporalCell = ({
  columnIndex,
  style,
  ranges,
  setCellWidth,
}: CellComponentProps<{
  ranges: Temporal["ranges"];
  setCellWidth: DynamicCellWidth["setCellWidth"];
}>) => {
  const ref = useRef<HTMLSpanElement>(null);
  const range = ranges[columnIndex];
  const isFirst = columnIndex === 0;
  const isLast = columnIndex === ranges.length - 1;

  useEffect(() => {
    if (ref.current) {
      setCellWidth(columnIndex, ref.current.offsetWidth);
    }
  }, [columnIndex, setCellWidth]);

  return (
    <div
      style={style}
      key={columnIndex}
    >
      <TemporalRangeShell
        cellIndex={columnIndex}
        measureRef={ref}
        isFirst={isFirst}
        isLast={isLast}
      >
        <TemporalRangeContent
          range={range}
          index={columnIndex}
        />
      </TemporalRangeShell>
    </div>
  );
};

const TemporalInlineList = ({ ranges }: { ranges: Temporal["ranges"] }) => (
  <span
    data-temporal-value-part="text-view-inline-list"
    style={{
      display: "inline-flex",
      maxWidth: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      paddingBottom: SCROLL_PADDING_Y,
      paddingTop: SCROLL_PADDING_Y,
    }}
  >
    {ranges.map((range, index) => (
      <TemporalRangeShell
        key={`${range.r.start}-${range.r.end}-${index}`}
        isFirst={index === 0}
        isLast={index === ranges.length - 1}
      >
        <TemporalRangeContent
          range={range}
          index={index}
        />
      </TemporalRangeShell>
    ))}
  </span>
);

export const TemporalTextView = ({ value, maxEntries = 0 }: TemporalTextProps) => {
  const ranges = (value as any).temporal ? (value as TemporalValueType).temporal.ranges : (value as Temporal).ranges;
  const visibleRanges = maxEntries === 0 ? ranges : ranges.slice(0, maxEntries);
  const gridRef = useRef<React.ComponentRef<typeof Grid>>(null);
  const getDefaultWidth = useCallback(() => DEFAULT_CELL_WIDTH, []);
  const cellWidth = useDynamicCellWidth({
    defaultCellWidth: getDefaultWidth,
    key: visibleRanges.length
      ? `${visibleRanges.length}-${visibleRanges[0].r.start}-${visibleRanges.at(-1)?.r.end}`
      : "empty",
  });

  if (visibleRanges.length < VIRTUALIZED_THRESHOLD) {
    return <TemporalInlineList ranges={visibleRanges} />;
  }

  return (
    <Grid
      gridRef={gridRef}
      columnWidth={(index) => cellWidth.getCellWidth(index) ?? cellWidth.getAverageCellWidth()}
      columnCount={visibleRanges.length}
      rowHeight={24}
      rowCount={1}
      cellComponent={TemporalCell}
      cellProps={{ ranges: visibleRanges, setCellWidth: cellWidth.setCellWidth }}
      style={{
        overflowX: "auto",
        overflowY: "hidden",
        paddingBottom: SCROLL_PADDING_Y,
        paddingTop: SCROLL_PADDING_Y,
      }}
    />
  );
};

export default TemporalTextView;
